/**
 * Settings → Database panel.
 *
 * Lists rows from /api/databases with row-level edit / disable / delete
 * plus an "Add database" button. The API gates writes behind the admin
 * role; the modal only routes here for admins anyway.
 */
import { useEffect, useState } from "react";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import {
  ActionIcon,
  Button,
  Group,
  Loader,
  MultiSelect,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
  Textarea,
  Tooltip,
} from "@mantine/core";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import {
  IconDatabasePlus,
  IconEdit,
  IconShieldLock,
  IconTrash,
} from "@tabler/icons-react";
import type { ApiClient } from "../../api/client";
import type { RegisteredDatabase } from "../../api/types";

type Form = {
  displayName: string;
  server: string;
  databaseName: string;
  description: string;
  enabled: boolean;
  schemas: string[];
};

export function DatabasesPanel({ api }: { api: ApiClient }) {
  const qc = useQueryClient();

  const dbsQ = useQuery({
    queryKey: ["databases"],
    queryFn: () => api.get<RegisteredDatabase[]>("/api/databases"),
  });

  const createMut = useMutation({
    mutationFn: (input: Partial<Form>) =>
      api.post<RegisteredDatabase>("/api/databases", input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["databases"] });
      notifications.show({ message: "Database registered", color: "teal" });
    },
    onError: (err) =>
      notifications.show({ message: `Could not register: ${(err as Error).message}`, color: "red" }),
  });

  const updateMut = useMutation({
    mutationFn: ({ id, input }: { id: string; input: Partial<Form> }) =>
      api.patch<RegisteredDatabase>(`/api/databases/${id}`, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["databases"] });
      notifications.show({ message: "Database updated", color: "teal" });
    },
    onError: (err) =>
      notifications.show({ message: `Could not update: ${(err as Error).message}`, color: "red" }),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => api.del(`/api/databases/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["databases"] });
      notifications.show({ message: "Database removed", color: "teal" });
    },
    onError: (err) =>
      notifications.show({ message: `Could not delete: ${(err as Error).message}`, color: "red" }),
  });

  // Bootstrap the three Entra-group SQL principals + their DML grants
  // on the registered DB. Runs idempotently as the calling admin via
  // OBO — they need to be a SQL Entra admin on the server.
  const setupGrantsMut = useMutation({
    mutationFn: (id: string) =>
      api.post<{ created: string[]; granted: string[]; schemas: string[] }>(
        `/api/databases/${id}/setup-grants`,
        {},
      ),
    onSuccess: (res) => {
      const c = res.created.length;
      const g = res.granted.length;
      notifications.show({
        message:
          c === 0
            ? `All ${g} grants verified — no changes needed.`
            : `Created ${c} SQL user${c === 1 ? "" : "s"}; applied ${g} grants.`,
        color: "teal",
      });
    },
    onError: (err) =>
      notifications.show({
        message: `Setup failed: ${(err as Error).message}`,
        color: "red",
      }),
  });

  function openForm(initial: RegisteredDatabase | null) {
    modals.open({
      title: (
        <Text fw={700} fz="md">
          {initial ? "Edit registered database" : "Register a new database"}
        </Text>
      ),
      size: "md",
      children: (
        <DatabaseForm
          api={api}
          initial={initial}
          onSubmit={async (values) => {
            if (initial) {
              await updateMut.mutateAsync({ id: initial.id, input: values });
            } else {
              await createMut.mutateAsync(values);
            }
            modals.closeAll();
          }}
          onCancel={() => modals.closeAll()}
        />
      ),
    });
  }

  function confirmDelete(d: RegisteredDatabase) {
    modals.openConfirmModal({
      title: <Text fw={700} fz="md">Remove {d.displayName}?</Text>,
      children: (
        <Text size="sm">
          This unregisters the database from this app. The underlying SQL database is not touched.
        </Text>
      ),
      labels: { confirm: "Remove", cancel: "Cancel" },
      confirmProps: { color: "red" },
      onConfirm: () => deleteMut.mutate(d.id),
    });
  }

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-start">
        <Text size="sm" c="dimmed">
          Databases users can pick from the topbar switcher. Registering doesn't
          grant data access — SQL permissions still apply per-user.
        </Text>
        <Button leftSection={<IconDatabasePlus size={14} />} onClick={() => openForm(null)} size="sm">
          Add database
        </Button>
      </Group>

      <Table withRowBorders highlightOnHover verticalSpacing="xs">
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Display name</Table.Th>
            <Table.Th>Server</Table.Th>
            <Table.Th>Database</Table.Th>
            <Table.Th style={{ width: 90 }}>Enabled</Table.Th>
            <Table.Th style={{ width: 130 }} />
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {dbsQ.data?.map((d) => (
            <Table.Tr key={d.id}>
              <Table.Td>{d.displayName}</Table.Td>
              <Table.Td><code>{d.server}</code></Table.Td>
              <Table.Td><code>{d.databaseName}</code></Table.Td>
              <Table.Td>
                <Switch
                  size="sm"
                  checked={d.enabled}
                  onChange={(e) => updateMut.mutate({ id: d.id, input: { enabled: e.currentTarget.checked } })}
                />
              </Table.Td>
              <Table.Td>
                <Group gap={4} wrap="nowrap" justify="flex-end">
                  <Tooltip label="Set up data access (creates SQL users + grants)">
                    <ActionIcon
                      variant="subtle"
                      color="indigo"
                      onClick={() => setupGrantsMut.mutate(d.id)}
                      loading={setupGrantsMut.isPending && setupGrantsMut.variables === d.id}
                    >
                      <IconShieldLock size={14} />
                    </ActionIcon>
                  </Tooltip>
                  <Tooltip label="Edit">
                    <ActionIcon variant="subtle" onClick={() => openForm(d)}>
                      <IconEdit size={14} />
                    </ActionIcon>
                  </Tooltip>
                  <Tooltip label="Remove">
                    <ActionIcon variant="subtle" color="red" onClick={() => confirmDelete(d)}>
                      <IconTrash size={14} />
                    </ActionIcon>
                  </Tooltip>
                </Group>
              </Table.Td>
            </Table.Tr>
          ))}
          {dbsQ.data && dbsQ.data.length === 0 && (
            <Table.Tr>
              <Table.Td colSpan={5}>
                <Text size="sm" c="dimmed" ta="center" py="md">No databases registered.</Text>
              </Table.Td>
            </Table.Tr>
          )}
        </Table.Tbody>
      </Table>
    </Stack>
  );
}

function DatabaseForm({
  api,
  initial,
  onSubmit,
  onCancel,
}: {
  api: ApiClient;
  initial: RegisteredDatabase | null;
  onSubmit: (values: Form) => Promise<void>;
  onCancel: () => void;
}) {
  const [v, setV] = useState<Form>({
    displayName: initial?.displayName ?? "",
    server: initial?.server ?? "",
    databaseName: initial?.databaseName ?? "",
    description: initial?.description ?? "",
    enabled: initial?.enabled ?? true,
    schemas: initial?.schemas ?? ["dbo"],
  });
  const [submitting, setSubmitting] = useState(false);
  const [availableSchemas, setAvailableSchemas] = useState<string[] | null>(null);
  const [discovering, setDiscovering] = useState(false);
  const [discoverError, setDiscoverError] = useState<string | null>(null);

  // Discover schemas in the target DB by issuing a server-side query
  // via OBO. The admin's own SQL rights are what authorize the lookup.
  // Required so the MultiSelect knows what options to offer; the user
  // can also type a name not in the list (creatable).
  const discoverSchemas = async () => {
    if (!v.server || !v.databaseName) return;
    setDiscovering(true);
    setDiscoverError(null);
    try {
      const list = await api.post<string[]>("/api/databases/list-schemas", {
        server: v.server,
        databaseName: v.databaseName,
      });
      setAvailableSchemas(list);
      // Make sure currently-selected schemas appear as options even if
      // the discovery returns a different set (admin may have stale
      // values).
      const merged = Array.from(new Set([...list, ...v.schemas]));
      if (merged.length !== list.length) setAvailableSchemas(merged);
    } catch (err) {
      setDiscoverError((err as Error).message);
    } finally {
      setDiscovering(false);
    }
  };

  // Auto-discover for the Edit case (we already have a server+db). For
  // the Add case, the admin has to fill those in first and click the
  // Discover button.
  useEffect(() => {
    if (initial && v.server && v.databaseName) {
      void discoverSchemas();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const schemaOptions = availableSchemas ?? v.schemas;

  return (
    <form
      onSubmit={async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try { await onSubmit(v); }
        finally { setSubmitting(false); }
      }}
    >
      <Stack gap="sm">
        <TextInput label="Display name" required value={v.displayName} onChange={(e) => setV({ ...v, displayName: e.currentTarget.value })} placeholder="e.g. KSE DW Dev" />
        <TextInput label="Server" required value={v.server} onChange={(e) => setV({ ...v, server: e.currentTarget.value })} placeholder="e.g. ksesql01.database.windows.net" />
        <TextInput label="Database" required value={v.databaseName} onChange={(e) => setV({ ...v, databaseName: e.currentTarget.value })} placeholder="e.g. ksdwnew_dev" />

        <div>
          <Group justify="space-between" align="flex-end" mb={4}>
            <Text size="sm" fw={500}>Schemas</Text>
            <Button
              size="compact-xs"
              variant="subtle"
              onClick={discoverSchemas}
              loading={discovering}
              disabled={!v.server || !v.databaseName}
            >
              {availableSchemas ? "Refresh" : "Discover"}
            </Button>
          </Group>
          <MultiSelect
            data={schemaOptions}
            value={v.schemas}
            onChange={(vals) => setV({ ...v, schemas: vals })}
            placeholder={
              availableSchemas
                ? "Pick the schemas the app should manage…"
                : "Click Discover to load the list"
            }
            searchable
            clearable
            nothingFoundMessage="No schemas found"
            description={
              discoverError
                ? `Discovery failed: ${discoverError}`
                : "Used by Set up data access — readers/editors get DML on these schemas only."
            }
            error={discoverError ?? undefined}
            rightSection={discovering ? <Loader size="xs" /> : undefined}
          />
        </div>

        <Textarea label="Description" autosize minRows={2} value={v.description} onChange={(e) => setV({ ...v, description: e.currentTarget.value })} />
        <Switch label="Enabled (visible in switcher)" checked={v.enabled} onChange={(e) => setV({ ...v, enabled: e.currentTarget.checked })} />
        <Group justify="flex-end" gap="xs" mt="sm">
          <Button variant="default" onClick={onCancel} disabled={submitting}>Cancel</Button>
          <Button type="submit" loading={submitting}>{initial ? "Save changes" : "Register"}</Button>
        </Group>
      </Stack>
    </form>
  );
}
